import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ExternalLink, GripVertical, Loader2, Star } from 'lucide-react';
import UnitPhotoCarousel from '../components/tenant/UnitPhotoCarousel';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useTenantApiToken, useTenantProfile } from '../contexts/TenantProfileContext';
import {
  addProspectShortlistUnit,
  getProspectShortlist,
  importProspectShortlistUnits,
  removeProspectShortlistUnit,
  reorderProspectShortlist,
  type ListedUnitCard,
  type ProspectShortlistPage,
  type ShortlistUnitCard,
} from '../services/tenantApi';
import { shortlistProgress } from '../utils/prospect-sidebar-progress';
import { clearWebsiteFavoriteUnitIds, peekWebsiteFavoriteUnitIds } from '../utils/websiteFavorites';

const SHORTLIST_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-none';
const SECTION_CARD =
  'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5';
const CARD_DESCRIPTION = 'mt-1 text-sm text-gray-500 dark:text-gray-400';

const BEDROOM_FILTERS = [
  { key: '1', label: '1', matches: (bedrooms: number) => bedrooms === 1 },
  { key: '2', label: '2', matches: (bedrooms: number) => bedrooms === 2 },
  { key: '3', label: '3', matches: (bedrooms: number) => bedrooms === 3 },
  { key: '4', label: '4', matches: (bedrooms: number) => bedrooms === 4 },
  { key: '5', label: '5', matches: (bedrooms: number) => bedrooms === 5 },
  { key: '6', label: '6', matches: (bedrooms: number) => bedrooms === 6 },
  { key: '7+', label: '7+', matches: (bedrooms: number) => bedrooms >= 7 },
] as const;

type BedroomFilterKey = (typeof BEDROOM_FILTERS)[number]['key'];

const ALL_BEDROOM_FILTERS: BedroomFilterKey[] = BEDROOM_FILTERS.map((option) => option.key);

function preferenceBedroomFilters(
  prospect: { prospectGroup?: { productPreferences?: { stated: { values: { bedrooms: string[] } } } | null } | null } | null,
): BedroomFilterKey[] {
  const saved = prospect?.prospectGroup?.productPreferences?.stated.values.bedrooms ?? [];
  const allowed = new Set<string>(ALL_BEDROOM_FILTERS);
  const selected: BedroomFilterKey[] = [];
  for (const value of saved) {
    const key = value.trim();
    if (!allowed.has(key) || selected.includes(key as BedroomFilterKey)) continue;
    selected.push(key as BedroomFilterKey);
  }
  return selected;
}

function unitMatchesBedroomFilter(unit: ListedUnitCard, selected: readonly string[]): boolean {
  const sizes = unit.bedroomFilters?.length ? unit.bedroomFilters : [unit.bedrooms];
  return sizes.some((bedrooms) =>
    BEDROOM_FILTERS.some((option) => selected.includes(option.key) && option.matches(bedrooms)),
  );
}

const PRIORITY_LABELS = ['Top pick', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

function shortlistPriorityLabel(rank: number): string {
  return PRIORITY_LABELS[rank - 1] ?? String(rank);
}

function formatLocation(unit: ListedUnitCard): string | null {
  const city = unit.city?.trim() ?? '';
  const province = unit.province?.trim() ?? '';
  if (city && province) return `${city}, ${province}`;
  return city || province || null;
}

function SectionCheck({ done }: { done: boolean }): ReactElement | null {
  if (!done) return null;
  return (
    <span
      className="absolute right-4 top-4 z-10 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 sm:right-5 sm:top-5"
      aria-label="Complete"
    >
      <Check size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

function formatShortlistPrice(unit: ListedUnitCard): string {
  const parts = shortlistPriceParts(unit);
  return parts.suffix ? `${parts.amount} ${parts.suffix}` : parts.amount;
}

function shortlistPriceParts(unit: ListedUnitCard): { amount: string; suffix: string | null } {
  if (unit.building) {
    if (unit.price > 0) return { amount: `$${Math.round(unit.price).toLocaleString()}`, suffix: '/ bed / mo' };
    return { amount: 'Rent on request', suffix: null };
  }
  if (unit.bedrooms > 0 && unit.price > 0) {
    const perBed = Math.round(unit.price / unit.bedrooms);
    return { amount: `$${perBed.toLocaleString()}`, suffix: '/ bed / mo' };
  }
  if (unit.price > 0) return { amount: `$${unit.price.toLocaleString()}`, suffix: '/ mo' };
  return { amount: 'Rent on request', suffix: null };
}

function formatAvailableDate(ymd: string | null): string | null {
  if (!ymd) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function unitFacts(unit: ListedUnitCard): string {
  if (unit.building) {
    const available = formatAvailableDate(unit.availableDate);
    return available ? `1–4 bed · Available ${available}` : '1–4 bed';
  }
  const facts = [`${unit.bedrooms} bed`, `${unit.bathrooms} bath`];
  if (unit.sqft) facts.push(`${unit.sqft.toLocaleString()} sqft`);
  const available = formatAvailableDate(unit.availableDate);
  if (available) facts.push(`Available ${available}`);
  return facts.join(' · ');
}

function availableFact(ymd: string | null): { value: string; label: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? '');
  if (!match) return { value: '—', label: 'Avail.' };
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return { value: '—', label: 'Avail.' };
  return {
    value: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    label: 'Avail.',
  };
}

function ShortlistFactsBox({ unit }: { unit: ListedUnitCard }): ReactElement {
  const available = availableFact(unit.availableDate);
  const cells = unit.building
    ? [
        { value: '1–4', label: 'beds' },
        { value: '1–4', label: 'baths' },
        { value: '\u00A0', label: 'sqft' },
        available,
      ]
    : [
        { value: String(unit.bedrooms), label: unit.bedrooms === 1 ? 'bed' : 'beds' },
        { value: String(unit.bathrooms), label: unit.bathrooms === 1 ? 'bath' : 'baths' },
        { value: unit.sqft ? unit.sqft.toLocaleString() : '—', label: 'sqft' },
        available,
      ];
  return (
    <div className="grid w-[8.25rem] shrink-0 grid-cols-2">
      {cells.map((cell) => (
        <div key={cell.label} className="px-2 py-1.5">
          <p className="text-xs font-semibold leading-tight text-gray-900 dark:text-white">{cell.value}</p>
          <p className="text-[10px] font-medium lowercase tracking-wide text-gray-400 dark:text-gray-500">{cell.label}</p>
        </div>
      ))}
    </div>
  );
}

function ShortlistPriceBox({ unit }: { unit: ListedUnitCard }): ReactElement {
  const price = shortlistPriceParts(unit);
  const caption = unit.building && unit.price > 0
    ? 'Per bed'
    : unit.bedrooms > 0 && unit.price > 0
      ? 'Per bed'
      : price.suffix
        ? 'Per month'
        : null;
  return (
    <div className="flex w-[4.75rem] shrink-0 flex-col items-center justify-center px-2 py-1.5 text-center">
      <p className="text-sm font-semibold leading-tight text-gray-900 dark:text-white">{price.amount}</p>
      {caption ? <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{caption}</p> : null}
    </div>
  );
}

function ShortlistStarButton({
  disabled,
  label,
  onClick,
}: {
  disabled: boolean;
  label: string;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      onClick={onClick}
      className="group relative inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-500 hover:bg-amber-50 disabled:opacity-50 dark:hover:bg-amber-500/10"
    >
      <Star className="h-4 w-4 fill-amber-400 text-amber-500 transition group-hover:fill-transparent group-focus-visible:fill-transparent" aria-hidden="true" />
      <span className="pointer-events-none absolute right-full z-20 mr-1.5 hidden whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white group-hover:block group-focus-visible:block">
        Remove
      </span>
    </button>
  );
}

function ListingLink({ href, label, className = '' }: { href: string; label: string; className?: string }): ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white ${className}`}
    >
      {label}
      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
    </a>
  );
}

function ShortlistToggle({
  filled,
  disabled,
  label,
  onClick,
  removal = false,
}: {
  filled: boolean;
  disabled: boolean;
  label: string;
  onClick: () => void;
  removal?: boolean;
}): ReactElement {
  const text = filled ? (removal ? 'Remove' : 'Shortlisted') : 'Shortlist';
  const selected = filled && !removal;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={filled}
      aria-label={label}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
        selected
          ? 'border-gray-900 bg-gray-900 text-white'
          : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
      }`}
    >
      {removal ? null : (
        <Star className={`h-4 w-4 ${selected ? 'fill-amber-300 text-amber-300' : 'text-gray-500'}`} aria-hidden="true" />
      )}
      {text}
    </button>
  );
}

function UnitCover({ imageUrl, className }: { imageUrl: string | null; className: string }): ReactElement {
  return (
    <div className={`overflow-hidden bg-gray-100 ${className}`}>
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-400 dark:text-gray-500">No photo</div>
      )}
    </div>
  );
}

function unitPhotos(unit: ListedUnitCard): string[] {
  if (unit.photos?.length) return unit.photos;
  return unit.imageUrl ? [unit.imageUrl] : [];
}

function SortableShortlistRow({
  unit,
  busy,
  onRemove,
}: {
  unit: ShortlistUnitCard;
  busy: boolean;
  onRemove: (unitId: string) => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: unit.id,
    disabled: busy,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const priority = shortlistPriorityLabel(unit.rank);
  const bedLabel = unit.building ? '1–4 beds' : `${unit.bedrooms} ${unit.bedrooms === 1 ? 'bed' : 'beds'}`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border px-2 py-2.5 sm:px-3 ${
        isDragging
          ? 'relative z-10 border-gray-900 bg-white shadow-bento dark:border-white dark:bg-gray-800'
          : unit.rank === 1
            ? 'border-red-300 bg-red-50/40 ring-1 ring-red-500/25 dark:border-red-500/40 dark:bg-red-950/20 dark:ring-red-400/20'
            : 'border-gray-200 bg-white dark:border-white/10 dark:bg-gray-800'
      }`}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          className="inline-flex h-10 w-8 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-400 hover:bg-gray-50 hover:text-gray-700 active:cursor-grabbing dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-200"
          aria-label={`Drag to reorder ${unit.title}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <a
          href={unit.listingUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View listing for ${unit.title}`}
          className="hidden shrink-0 rounded-lg outline-offset-2 transition hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900 sm:block"
        >
          <UnitCover imageUrl={unit.imageUrl} className="h-[4.5rem] w-20 rounded-lg" />
        </a>
        <div className="min-w-0 flex-1">
          <a
            href={unit.listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm font-semibold leading-snug text-gray-900 underline-offset-2 hover:text-red-700 hover:underline dark:text-white dark:hover:text-red-300 sm:truncate"
          >
            {unit.title}
          </a>
          <p
            className={`mt-0.5 hidden text-xs font-semibold uppercase tracking-wide sm:block ${
              unit.rank === 1 ? 'text-red-700 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            {priority}
          </p>
          <p className="mt-1 text-xs leading-snug text-gray-600 dark:text-gray-300 sm:hidden">
            <span
              className={
                unit.rank === 1
                  ? 'font-semibold uppercase tracking-wide text-red-700 dark:text-red-400'
                  : 'font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400'
              }
            >
              {priority}
            </span>
            <span className="text-gray-400 dark:text-gray-500"> · </span>
            <span className="lowercase">{bedLabel}</span>
            <span className="text-gray-400 dark:text-gray-500"> · </span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatShortlistPrice(unit)}</span>
          </p>
        </div>
        <div className="hidden shrink-0 items-stretch gap-2 sm:flex">
          <ShortlistFactsBox unit={unit} />
          <ShortlistPriceBox unit={unit} />
        </div>
        <ShortlistStarButton
          disabled={busy}
          label={`Remove ${unit.title} from shortlist`}
          onClick={() => onRemove(unit.id)}
        />
      </div>
    </li>
  );
}

function AvailableUnitCard({
  unit,
  shortlisted,
  busy,
  onToggle,
}: {
  unit: ListedUnitCard;
  shortlisted: boolean;
  busy: boolean;
  onToggle: (unitId: string, shortlisted: boolean) => void;
}): ReactElement {
  const location = formatLocation(unit);

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-800">
      <UnitPhotoCarousel
        photos={unitPhotos(unit)}
        label={unit.title}
        className="aspect-square w-full"
      />
      <div className="flex shrink-0 flex-col px-3 py-3">
        <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{unit.title}</h3>
        {location ? <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{location}</p> : null}
        <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">{formatShortlistPrice(unit)}</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{unitFacts(unit)}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-3">
          <ShortlistToggle
            filled={shortlisted}
            disabled={busy}
            label={shortlisted ? `Remove ${unit.title} from shortlist` : `Add ${unit.title} to shortlist`}
            onClick={() => onToggle(unit.id, shortlisted)}
          />
          <ListingLink href={unit.listingUrl} label="Listing" />
        </div>
      </div>
    </article>
  );
}

export default function ShortlistUnitsPage(): ReactElement {
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const { prospect, loading: profileLoading } = useTenantProfile();
  const [page, setPage] = useState<ProspectShortlistPage | null>(null);
  const [selectedBedrooms, setSelectedBedrooms] = useState<BedroomFilterKey[] | null>(null);
  const bedroomFilterTouched = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyUnitId, setBusyUnitId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const carriedOver = peekWebsiteFavoriteUnitIds();
      if (carriedOver.length > 0) {
        try {
          const imported = await importProspectShortlistUnits(getToken, carriedOver);
          clearWebsiteFavoriteUnitIds();
          setPage(imported);
          return;
        } catch {
          const next = await getProspectShortlist(getToken);
          const carried = new Set(carriedOver);
          if (next.shortlist.some((unit) => carried.has(unit.id))) {
            clearWebsiteFavoriteUnitIds();
          }
          setPage(next);
          return;
        }
      }
      const next = await getProspectShortlist(getToken);
      setPage(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load the shortlist.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!page) return;
    setPageProgress('/shortlist-units', shortlistProgress(page.shortlist.length));
  }, [page, setPageProgress]);

  const preferenceBedrooms = useMemo(() => preferenceBedroomFilters(prospect), [prospect]);

  useEffect(() => {
    if (bedroomFilterTouched.current || profileLoading) return;
    setSelectedBedrooms(preferenceBedrooms.length > 0 ? preferenceBedrooms : ALL_BEDROOM_FILTERS);
  }, [preferenceBedrooms, profileLoading]);

  const visibleAvailable = useMemo(() => {
    if (!page || !selectedBedrooms) return [];
    return page.available.filter((unit) => unitMatchesBedroomFilter(unit, selectedBedrooms));
  }, [page, selectedBedrooms]);

  const toggleBedroomFilter = (key: BedroomFilterKey) => {
    bedroomFilterTouched.current = true;
    setSelectedBedrooms((current) => {
      const selected = new Set(current ?? []);
      if (selected.has(key)) selected.delete(key);
      else selected.add(key);
      return ALL_BEDROOM_FILTERS.filter((option) => selected.has(option));
    });
  };

  const showAllBedrooms = () => {
    bedroomFilterTouched.current = true;
    setSelectedBedrooms((current) =>
      current?.length === ALL_BEDROOM_FILTERS.length ? [] : [...ALL_BEDROOM_FILTERS],
    );
  };

  const shortlistedIds = useMemo(
    () => new Set(page?.shortlist.map((unit) => unit.id) ?? []),
    [page?.shortlist],
  );

  const toggleUnit = async (unitId: string, shortlisted: boolean) => {
    if (busyUnitId || reordering || !page) return;
    setBusyUnitId(unitId);
    setError(null);
    try {
      const everyListedHomeIsShortlisted =
        page.available.length > 0 && page.available.every((unit) => shortlistedIds.has(unit.id));
      if (everyListedHomeIsShortlisted && shortlisted) {
        let next = page;
        for (const other of page.shortlist.filter((unit) => unit.id !== unitId)) {
          next = await removeProspectShortlistUnit(getToken, other.id);
        }
        setPage(next);
        return;
      }
      const next = shortlisted
        ? await removeProspectShortlistUnit(getToken, unitId)
        : await addProspectShortlistUnit(getToken, unitId);
      setPage(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the shortlist.');
      await load();
    } finally {
      setBusyUnitId(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!page || reordering) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = page.shortlist.findIndex((unit) => unit.id === active.id);
    const newIndex = page.shortlist.findIndex((unit) => unit.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(page.shortlist, oldIndex, newIndex).map((unit, index) => ({
      ...unit,
      rank: index + 1,
    }));
    setPage({ ...page, shortlist: moved });
    setReordering(true);
    setError(null);
    try {
      const next = await reorderProspectShortlist(
        getToken,
        moved.map((unit) => unit.id),
      );
      setPage(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save the new order.');
      await load();
    } finally {
      setReordering(false);
    }
  };

  const shortlistCount = page?.shortlist.length ?? 0;
  const allBedroomsSelected = selectedBedrooms?.length === ALL_BEDROOM_FILTERS.length;
  const bedroomCopy =
    preferenceBedrooms.length > 0
      ? 'Sizes from your group preferences start on. Choose All to see every listed home.'
      : 'Your group has not chosen bedroom sizes yet, so every listed home starts visible.';

  return (
    <div
      className={`${SHORTLIST_SHELL} flex flex-col gap-4 pb-[max(6rem,calc(env(safe-area-inset-bottom)+5rem))] lg:h-[calc(100dvh-3rem)] lg:min-h-0 lg:overflow-hidden lg:pb-6`}
    >
      <header className={`shrink-0 ${SECTION_CARD}`}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 3</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">Shortlist Units</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          Add the homes you want, then drag your shortlist so the top pick is first.
          {page ? ` This list is shared with ${page.groupName}.` : ''}
        </p>
      </header>

      {error ? (
        <p className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{error}</p>
      ) : null}

      {loading ? (
        <p className={`flex items-center gap-2 text-sm text-gray-500 ${SECTION_CARD}`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading homes…
        </p>
      ) : (
        <div className="flex flex-col gap-4 lg:grid lg:h-full lg:min-h-0 lg:flex-1 lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:items-stretch">
          <section className={`relative flex min-h-0 flex-col lg:h-full lg:min-h-0 lg:min-w-0 lg:overflow-hidden ${SECTION_CARD}`}>
            <SectionCheck done={shortlistCount > 0} />
            <div className="shrink-0 pr-10">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Your shortlist</h2>
              <p className={CARD_DESCRIPTION}>
                {shortlistCount === 0
                  ? 'Nothing here yet. Shortlist a home from the available homes.'
                  : `${shortlistCount} ${shortlistCount === 1 ? 'home' : 'homes'}, in the order your group ranked them.`}
              </p>
              {reordering ? (
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving order…
                </p>
              ) : null}
            </div>
            {page && page.shortlist.length > 0 ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleDragEnd(event)}>
                <SortableContext items={page.shortlist.map((unit) => unit.id)} strategy={verticalListSortingStrategy}>
                  <ol className="mt-4 space-y-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
                    {page.shortlist.map((unit) => (
                      <SortableShortlistRow
                        key={unit.id}
                        unit={unit}
                        busy={busyUnitId === unit.id || reordering}
                        onRemove={(unitId) => void toggleUnit(unitId, true)}
                      />
                    ))}
                  </ol>
                </SortableContext>
              </DndContext>
            ) : (
              <p className="mt-4 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-white/10 dark:text-gray-400">
                No homes yet. Shortlist a home from the available homes.
              </p>
            )}
          </section>

          <section className={`flex min-h-0 flex-col lg:h-full lg:min-h-0 lg:min-w-0 lg:overflow-hidden ${SECTION_CARD}`}>
            <div className="shrink-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Available homes</h2>
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">Search listed homes</p>
            <p className={CARD_DESCRIPTION}>{bedroomCopy}</p>
            <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Search listed homes by bedroom count">
              <button
                type="button"
                aria-pressed={allBedroomsSelected}
                onClick={showAllBedrooms}
                className={`min-w-[2.25rem] rounded-lg border px-2.5 py-1.5 text-xs font-medium lowercase ${
                  allBedroomsSelected
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-white/5'
                }`}
              >
                all
              </button>
              {BEDROOM_FILTERS.map((option) => {
                const selected = selectedBedrooms?.includes(option.key) ?? false;
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${option.label} bed${option.key === '1' ? '' : 's'}`}
                    onClick={() => toggleBedroomFilter(option.key)}
                    className={`min-w-[2.25rem] rounded-lg border px-2.5 py-1.5 text-xs font-medium tabular-nums ${
                      selected
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-white/5'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {!page || !selectedBedrooms
                ? 'Listed homes you can add to the shortlist.'
                : page.available.length === 0
                  ? 'Check back later for more options as they become available.'
                  : visibleAvailable.length === 0
                    ? 'No listed homes match this search. Turn another size on, or choose All.'
                    : `${visibleAvailable.length} listed ${visibleAvailable.length === 1 ? 'home matches' : 'homes match'} this search.`}
            </p>
            </div>
            {page && selectedBedrooms && page.available.length > 0 && visibleAvailable.length > 0 ? (
              <div className="mt-4 min-h-0 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleAvailable.map((unit) => (
                    <AvailableUnitCard
                      key={unit.id}
                      unit={unit}
                      shortlisted={shortlistedIds.has(unit.id)}
                      busy={busyUnitId === unit.id || reordering}
                      onToggle={(unitId, shortlisted) => void toggleUnit(unitId, shortlisted)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
